import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { catalogService } from '../api/catalog';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { ProductCategory } from '@/types/catalog';

// The catalogue is static reference data — a product's name, code, axis list
// and colour value never change under us. Cache it for the session instead of
// refetching on every picker open.
const STATIC_STALE_TIME = 24 * 60 * 60 * 1000;

/**
 * Which libraries exist, how deep each goes and what it is faceted by.
 *
 * The picker reads this instead of carrying its own list of categories: adding
 * roofing is then a migration and a seed, not a web release.
 */
export function useCatalogCategories() {
  return useQuery({
    queryKey: cacheKeys.catalog.categories(),
    queryFn: () => catalogService.getCategories(),
    staleTime: STATIC_STALE_TIME,
    gcTime: STATIC_STALE_TIME,
  });
}

/**
 * The brand shelf, arranged for a country.
 *
 * `country` is the explicit filter the user chose; with none, the API still
 * sorts the makers known to sell where their house is to the top and reports
 * which country that was. The country is part of the cache key because the two
 * answers are different lists, not the same list re-sorted.
 */
export function useCatalogBrands(
  category: string | null,
  country: string | null = null
) {
  return useQuery({
    queryKey: cacheKeys.catalog.brands(category ?? '', country),
    queryFn: () => catalogService.getBrands(category!, country),
    enabled: category !== null,
    staleTime: STATIC_STALE_TIME,
    gcTime: STATIC_STALE_TIME,
  });
}

export function useCatalogLines(category: string | null, brandId: string | null) {
  return useQuery({
    queryKey: cacheKeys.catalog.lines(category ?? '', brandId),
    queryFn: () => catalogService.getLines({ category: category!, brandId }),
    enabled: category !== null && brandId !== null,
    staleTime: STATIC_STALE_TIME,
    gcTime: STATIC_STALE_TIME,
  });
}

/**
 * A product list, paged by cursor.
 *
 * `search` is expected to be already debounced by the caller — the query key is
 * the search term, so every keystroke would otherwise be its own request and
 * its own cache entry.
 *
 * `axisValue` is not a cosmetic filter: it runs the manufacturer's published
 * availability matrix, so the list is what can actually be ordered on that
 * geometry.
 */
export function useCatalogProducts(params: {
  category: string | null;
  search: string;
  brandId?: string | null;
  lineId?: string | null;
  axisValue?: string | null;
  colorFamily?: string | null;
  enabled?: boolean;
}) {
  const {
    category,
    search,
    brandId = null,
    lineId = null,
    axisValue = null,
    colorFamily = null,
    enabled = true,
  } = params;

  const query = useInfiniteQuery({
    queryKey: cacheKeys.catalog.products(
      category ?? '',
      search,
      brandId,
      lineId,
      axisValue,
      colorFamily
    ),
    queryFn: ({ pageParam }) =>
      catalogService.getProducts({
        category: category!,
        search,
        brandId,
        lineId,
        axisValue,
        colorFamily,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && category !== null,
    staleTime: STATIC_STALE_TIME,
    gcTime: STATIC_STALE_TIME,
  });

  const products = useMemo(
    () => query.data?.pages.flatMap((page) => page.products) ?? [],
    [query.data]
  );

  const notices = query.data?.pages[0]?.notices ?? null;

  return { ...query, products, notices };
}

/**
 * The token-bearing axes of a category — the ones a pick is made ON.
 *
 * Siding has exactly one (profile), paint has none, tile will have two; the
 * picker offers a chip row per axis it is given.
 *
 * A hook rather than a plain function because the result feeds a dependency
 * array: `.filter()` hands back a new array on every render, and downstream
 * memos and effects keyed on it would re-run for the whole life of the picker.
 */
export function useTokenBearingAxes(category: ProductCategory | undefined) {
  const axes = category?.attributes.axes;

  return useMemo(
    () => (axes ?? []).filter((axis) => axis.attributes.is_token_bearing),
    [axes]
  );
}
